import { Spacing } from '@persona-id/design-tokens';
import { isEmpty } from 'lodash';
import { keyBy } from 'lodash/fp';
import {
  ComponentProps,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Waypoint } from 'react-waypoint';
import { reduxForm } from 'redux-form';
import styled from 'styled-components';

import {
  DEFAULT_SCREEN_DIMENSIONS,
  INQUIRY_TEMPLATE_VERSION_DIFF_FORM_NAME,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import {
  PreviewPageMap,
  StepPreviewMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { ScreenEditorDroppableScreen } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenEditorDroppableScreen';
import { SelectedComponentAndStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { ScreenItemConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/types';
import {
  mapToNavItemConfig,
  SKIPPED_PAGE_REASONS,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import InquiryTemplateScreenPreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview';
import StepDiffPropertyCard from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSteps/components/StepDiffPropertyCard';
import { VersionDiff } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { VersionDiffNavigationContext } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/utils';
import ScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper';
import { InquiryStatus } from 'dashboard/models/inquiry';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import Card from 'design-system/components/Card';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { TransitionParams } from 'inquiry/lib/types';
import { GOVERNMENT_ID_DESIGN_VERSION } from 'lib/constants/governmentIds';
import useToggle from 'lib/hooks/useToggle';
import { isUiStep, StepPreview } from 'lib/interfaces/StepsConfig';

const SCROLL_OFFSET = 10;
const FlowDiffContainer = styled.div`
  height: 900px;
  display: flex;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  border: 1px solid #e6e6ea;
`;

const StepDiffPanel = styled.div`
  overflow-y: auto;
  width: 30%;
  background: ${({ theme }) => theme.color.background.primary};
  padding-bottom: ${Spacing[4]};
`;

const PreviewContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.color.background.secondary};
  clip-path: inset(0 0 0 0);
`;

const PreviewWrapper = styled.div`
  height: 100%;
  margin: 40px 0px;
  align-items: center;
  display: flex;
  justify-content: center;

  width: 100%;
`;

const ScreensPreviewWrapper = styled.div`
  display: flex;
  width: 100%;

  // vertical border for the first direct child
  > div:first-child {
    border-right: 1px solid #e6e6ea;
  }
`;

const LabelContainer = styled.div`
  padding: ${Spacing[2]} ${Spacing[5]};
  border-bottom: 1px solid #e6e6ea;
  width: 50%;
`;

const LabelHeader = styled.div`
  display: flex;

  > div:last-child {
    border-right: 1px solid #e6e6ea;
  }
`;

const MissingScreenContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${DEFAULT_SCREEN_DIMENSIONS.height}px;
  width: ${DEFAULT_SCREEN_DIMENSIONS.width}px;
`;

// wrap rendered previews in redux from to pass validation of rendered form inputs
const ScreenPreviewsBase = ({ previews }: { previews: ReactNode[] }) => {
  return <>{previews}</>;
};

const ScreenPreviews = reduxForm<TransitionParams, { previews: ReactNode[] }>({
  form: INQUIRY_TEMPLATE_VERSION_DIFF_FORM_NAME,
})(ScreenPreviewsBase);

interface InquiryTemplateVersionDiffStepsProps {
  versionDiff: VersionDiff;
  targetInquiryTemplateVersion: InquiryTemplateVersion;
  targetPreviewPageMap: PreviewPageMap;
  targetStepsPreviewMap: StepPreviewMap;
  basePreviewPageMap: PreviewPageMap;
  baseStepsPreviewMap: StepPreviewMap;
}

const InquiryTemplateVersionDiffSteps = ({
  versionDiff,
  targetPreviewPageMap,
  targetStepsPreviewMap,
  basePreviewPageMap,
  baseStepsPreviewMap,
  targetInquiryTemplateVersion,
}: InquiryTemplateVersionDiffStepsProps) => {
  const [showUnchanged, { toggle: toggleShowUnchanged }] = useToggle(false);
  const { selectedStepDifferStep: selectedStep, setSelectedStepDifferStep: setSelectedStep } =
    useContext(VersionDiffNavigationContext);

  const [selectedComponentAndStep, setSelectedComponentAndStep] =
    useState<SelectedComponentAndStep>(null);

  const useGovIdRedesign =
    targetInquiryTemplateVersion.attributes['govid-design-version'] ===
    GOVERNMENT_ID_DESIGN_VERSION.V1;
  const { steps: targetSteps, theme: targetTheme } = targetInquiryTemplateVersion.attributes;
  const { steps: baseSteps } = versionDiff.baseInquiryTemplateVersionData;
  const { steps: stepChanges } = versionDiff.inquiryTemplateVersionDiff;
  const targetStepsMap = useMemo(() => keyBy('name', targetSteps), [targetSteps]);
  const baseStepsMap = useMemo(() => keyBy('name', baseSteps), [baseSteps]);
  const targetPages = useMemo(
    () => Object.values(targetPreviewPageMap).flatMap((pages) => pages),
    [targetPreviewPageMap],
  );
  const basePages = useMemo(
    () => Object.values(basePreviewPageMap).flatMap((pages) => pages),
    [basePreviewPageMap],
  );
  const baseStepsPreview = Object.values(baseStepsPreviewMap);
  const targetStepsPreview = Object.values(targetStepsPreviewMap);

  const targetScreenItemConfigs: ScreenItemConfig[] = useMemo(
    () => mapToNavItemConfig(targetSteps, useGovIdRedesign, targetStepsPreview),
    [targetSteps, useGovIdRedesign, targetStepsPreview],
  );
  const baseScreenItemConfigs: ScreenItemConfig[] = useMemo(
    () => mapToNavItemConfig(baseSteps, useGovIdRedesign, baseStepsPreview),
    [baseSteps, useGovIdRedesign, baseStepsPreview],
  );

  const previewRefMap = useRef<Record<string, RefObject<HTMLDivElement>>>({});
  const diffContainerRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const scrollToSelectedScreen = useCallback(
    (navConfig: ScreenItemConfig) => {
      if (!previewContainerRef.current || !navConfig) return;

      previewContainerRef.current.scrollTo({
        top:
          (previewRefMap.current?.[navConfig.key]?.current?.offsetTop || 0) -
          (diffContainerRef.current?.offsetTop || 0) -
          SCROLL_OFFSET,
        behavior: 'auto',
      });

      setSelectedStep(navConfig);
    },
    [previewContainerRef, previewRefMap, setSelectedStep],
  );

  const screenItemConfigs = useMemo(
    () => [
      ...targetScreenItemConfigs,
      ...baseScreenItemConfigs.filter(
        (baseItemConfig) =>
          targetScreenItemConfigs.find(
            (targetItemConfig) => baseItemConfig.stepToken === targetItemConfig.stepToken,
          ) == null,
      ),
    ],
    [baseScreenItemConfigs, targetScreenItemConfigs],
  );

  useEffect(() => {
    if (selectedStep) {
      scrollToSelectedScreen(selectedStep);
    }
  }, [selectedStep, scrollToSelectedScreen]);

  const ScreenPreview = useCallback(
    ({
      navItemConfig,
      page,
      step,
      stepPreview,
      theme,
      selected,
      selectedComponentAndStep,
    }: {
      navItemConfig: ScreenItemConfig;
      page: ReactElement;
      step: InquiryTemplateVersionStep | undefined;
      theme: ThemeAttributes;
      stepPreview: StepPreview;
      selected: boolean;
      selectedComponentAndStep: SelectedComponentAndStep;
    }) => {
      const isUiStepScreen = isUiStep(stepPreview);
      const skipReason =
        step?.type &&
        navItemConfig?.pageKeys?.[0] &&
        SKIPPED_PAGE_REASONS[step?.type]?.[navItemConfig?.pageKeys?.[0]];

      return (
        <ScreenFlowNodeWrapper
          data-name={`${page.key}`}
          data-test={`${navItemConfig.displayName}__screen-preview`}
          data-marketing-id='ite-screen-editor__preview'
          editable={false}
          node={
            <ScreenEditorDroppableScreen
              step={isUiStepScreen ? (step as InquiryTemplateVersionStepUi) : undefined}
              fieldSchema={[]}
              initialFieldSchema={[]}
              selected={selected}
              selectedComponentAndStep={selectedComponentAndStep}
              theme={theme}
              editable={false}
              startedComponentDrag={false}
              isUiStepScreen={isUiStepScreen}
              stepPreview={stepPreview}
              onComponentClick={(clickedComponentAndStep) => {
                if (clickedComponentAndStep?.step.name !== selectedStep?.stepToken) {
                  setSelectedStep(navItemConfig);
                }
                setSelectedComponentAndStep(clickedComponentAndStep);
              }}
              updateSteps={() => {}}
              {...(!isUiStepScreen && {
                fallbackScreen: (
                  <InquiryTemplateScreenPreview
                    skipReason={navItemConfig?.skip ? skipReason : undefined}
                    page={page}
                    selected={false}
                    step={stepPreview}
                    theme={theme}
                  />
                ),
              })}
            />
          }
          handleNodeClick={() => {
            scrollToSelectedScreen(navItemConfig);
            setSelectedComponentAndStep(null);
          }}
          title={navItemConfig.displayName}
          withSticker
          stepStatuses={
            (step?.config.statuses.map((status) => status.name) as InquiryStatus[]) || []
          }
        />
      );
    },
    [scrollToSelectedScreen, setSelectedComponentAndStep],
  );

  const generateScreenPreviews = useCallback(
    (selectedComponentAndStep: SelectedComponentAndStep) => {
      const previewForNavItem = (navItemConfig: ScreenItemConfig): ReactNode => {
        const basePage: ReactElement | undefined = basePages.find(
          (p) => p.key === navItemConfig.key,
        );
        const targetPage: ReactElement | undefined = targetPages.find(
          (p) => p.key === navItemConfig.key,
        );

        const targetStep = targetSteps.find((step) => step.name === navItemConfig.stepToken);
        const baseStep = baseSteps.find((step) => step.name === navItemConfig.stepToken);
        const targetStepPreview = targetStepsPreviewMap[navItemConfig.stepToken];
        const baseStepPreview = baseStepsPreviewMap[navItemConfig.stepToken];
        const selected =
          navItemConfig.key === selectedStep?.key &&
          navItemConfig.stepToken === selectedStep?.stepToken;
        const componentAndStep =
          selectedComponentAndStep?.step.name === navItemConfig.stepToken
            ? selectedComponentAndStep
            : null;

        return (
          <Waypoint
            key={navItemConfig.key}
            topOffset='49%'
            bottomOffset='49%'
            fireOnRapidScroll={false}
          >
            <ScreensPreviewWrapper
              ref={
                (previewRefMap.current[navItemConfig.key] ??= {
                  current: null,
                })
              }
            >
              <PreviewWrapper>
                {basePage != null ? (
                  <ScreenPreview
                    navItemConfig={navItemConfig}
                    page={basePage}
                    step={baseStep}
                    stepPreview={baseStepPreview}
                    theme={targetTheme}
                    selectedComponentAndStep={componentAndStep}
                    selected={selected}
                  />
                ) : (
                  <MissingScreenContainer>
                    <Paragraph>Screen does not exist</Paragraph>
                  </MissingScreenContainer>
                )}
              </PreviewWrapper>
              <PreviewWrapper>
                {targetPage != null ? (
                  <ScreenPreview
                    navItemConfig={navItemConfig}
                    page={targetPage}
                    step={targetStep}
                    stepPreview={targetStepPreview}
                    theme={targetTheme}
                    selectedComponentAndStep={componentAndStep}
                    selected={selected}
                  />
                ) : (
                  <MissingScreenContainer>
                    <Paragraph>Screen does not exist</Paragraph>
                  </MissingScreenContainer>
                )}
              </PreviewWrapper>
            </ScreensPreviewWrapper>
          </Waypoint>
        );
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '70%' }}>
          <LabelHeader>
            <LabelContainer>Previous Version</LabelContainer>
            <LabelContainer>Current Version</LabelContainer>
          </LabelHeader>
          <PreviewContainer ref={previewContainerRef}>
            <ScreenPreviews
              previews={screenItemConfigs.map((config) =>
                config.items
                  ? config.items.map((groupItemConfig) => previewForNavItem(groupItemConfig))
                  : previewForNavItem(config),
              )}
            />
          </PreviewContainer>
        </div>
      );
    },
    [
      ScreenPreview,
      basePages,
      baseSteps,
      baseStepsPreviewMap,
      screenItemConfigs,
      targetPages,
      targetSteps,
      targetStepsPreviewMap,
      targetTheme,
      selectedStep?.key,
      selectedStep?.stepToken,
    ],
  );

  const stepChangesEmpty =
    isEmpty(stepChanges) &&
    Object.values(stepChanges).every((stepDiff) => isEmpty(stepDiff.changes.components));

  const StepDiffPropertyCardSharedProps: Pick<
    ComponentProps<typeof StepDiffPropertyCard>,
    'setSelectedStep' | 'setSelectedComponentAndStep'
  > = {
    setSelectedStep,
    setSelectedComponentAndStep,
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={[
            <ToggleSlider
              key='show-unchanged-toggle'
              titleOn='displaying all values'
              titleOff='only showing changes'
              labelOn='Show unchanged'
              labelOff='Show unchanged'
              value={showUnchanged}
              onChange={toggleShowUnchanged}
            />,
          ]}
        >
          Screens
        </Card.Title>
        {stepChangesEmpty && !showUnchanged ? (
          <Label color='secondary' size='lg'>
            No changes made
          </Label>
        ) : (
          <>
            <FlowDiffContainer ref={diffContainerRef}>
              {generateScreenPreviews(selectedComponentAndStep)}
              <StepDiffPanel>
                {Object.entries(stepChanges).map(([stepName, stepDiff]) => {
                  let itemConfig = targetScreenItemConfigs.find(
                    (config) => config.stepToken === stepName,
                  );
                  if (!itemConfig)
                    itemConfig = baseScreenItemConfigs.find(
                      (config) => config.stepToken === stepName,
                    );

                  if (
                    (!targetStepsMap[stepName] || isUiStepConfig(targetStepsMap[stepName])) &&
                    (!baseStepsMap[stepName] || isUiStepConfig(baseStepsMap[stepName]))
                  ) {
                    return (
                      <StepDiffPropertyCard
                        key={stepName}
                        stepDiff={stepDiff}
                        targetStep={targetStepsMap[stepName] as InquiryTemplateVersionStepUi}
                        baseStep={baseStepsMap[stepName] as InquiryTemplateVersionStepUi}
                        targetStepsMap={targetStepsMap}
                        baseStepsMap={baseStepsMap}
                        screenItemConfig={itemConfig}
                        showUnchanged={showUnchanged}
                        isSelectedStep={selectedStep?.key === stepName}
                        selectedComponentName={
                          selectedStep?.key === stepName
                            ? selectedComponentAndStep?.component.name
                            : undefined
                        }
                        {...StepDiffPropertyCardSharedProps}
                      />
                    );
                  }
                })}
                {showUnchanged &&
                  Object.entries(targetStepsMap)
                    .filter(
                      ([stepName, step]) =>
                        stepChanges && !(stepName in stepChanges) && isUiStepConfig(step),
                    )
                    .map(([stepName, targetStep]) => {
                      const itemConfig = targetScreenItemConfigs.find(
                        (config) => config.stepToken === stepName,
                      );

                      return (
                        <StepDiffPropertyCard
                          isSelectedStep={selectedStep?.key === stepName}
                          key={stepName}
                          stepDiff={undefined}
                          targetStep={targetStep as InquiryTemplateVersionStepUi}
                          baseStep={baseStepsMap[stepName] as InquiryTemplateVersionStepUi}
                          targetStepsMap={targetStepsMap}
                          baseStepsMap={baseStepsMap}
                          screenItemConfig={itemConfig}
                          selectedComponentName={
                            selectedStep?.key === stepName
                              ? selectedComponentAndStep?.component.name
                              : undefined
                          }
                          {...StepDiffPropertyCardSharedProps}
                        />
                      );
                    })}
              </StepDiffPanel>
            </FlowDiffContainer>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default InquiryTemplateVersionDiffSteps;
